import { instrumentQueryKeys } from '@/config/queryKeys';
import { smIconSize } from '@/constants';
import { useDateFormatter } from '@/hooks';
import { deleteInstrument } from '@/services/instruments';
import type { InstrumentPublic } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Dialog } from '@nautilusbio/nb-design-system/Dialog';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader, Pencil, Trash2 } from 'lucide-react';
import React, { memo, useCallback, useRef, useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { InstrumentFormDialog } from './InstrumentFormDialog';
import styles from './styles.module.css';

interface ActionsCellRendererProps {
  data: InstrumentPublic;
  isDisabled?: boolean;
}

const ActionsCellRendererComponent: FC<ActionsCellRendererProps> = ({ data, isDisabled }) => {
  const { t } = useTranslation('instruments');
  const { t: tGeneral } = useTranslation('general');
  const formatDate = useDateFormatter();
  const queryClient = useQueryClient();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const toastIdRef = useRef<string | number | undefined>(undefined);

  const deleteMutation = useMutation({
    mutationFn: () => deleteInstrument(data.org_id, data.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: instrumentQueryKeys.list(data.org_id) });
      toast.success(t('delete_success', { name: data.name }), { id: toastIdRef.current });
      toastIdRef.current = undefined;
      setIsDeleteOpen(false);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[deleteInstrument]', message);
      toast.error(t('delete_error', { name: data.name, error: message }), { id: toastIdRef.current });
      toastIdRef.current = undefined;
    },
  });

  const handleDeleteConfirm = useCallback(() => {
    toastIdRef.current = toast.loading(t('delete_deleting', { name: data.name }));
    deleteMutation.mutate();
  }, [deleteMutation, data.name, t]);

  return (
    <Stack alignItems="center" gap={1} justifyContent="center" orientation="horizontal">
      {/* Edit */}
      <InstrumentFormDialog
        instrument={data}
        triggerProps={{
          children: <Tooltip content={t('edit_tooltip')} trigger={<Pencil size={smIconSize} />} />,
          isDisabled,
          size: 'sm',
          visual: 'unstyled',
        }}
      />

      {/* Delete */}
      <Dialog
        classNames={{
          footer: styles.footer,
        }}
        data-testid={`delete-instrument-dialog-${data.id}`}
        disableOutsideClick={deleteMutation.isPending}
        disableEscapeKey={deleteMutation.isPending}
        open={isDeleteOpen}
        onOpenChange={open => {
          if (!deleteMutation.isPending) setIsDeleteOpen(open);
        }}
        title={t('delete_dialog_title', { name: data.name })}
        content={
          <>
            <Text as="h3">{t('delete_dialog_description', { name: data.name })}</Text>
            <Text as="p">{t('delete_dialog_sub_description')}</Text>
            <Stack gap={1} orientation="horizontal">
              <Text size="sm" color="muted">
                {t('field_name')}:&nbsp;
              </Text>
              <Text size="sm">{data.name}</Text>
            </Stack>
            {data.serial_num && (
              <Stack gap={1} orientation="horizontal">
                <Text size="sm" color="muted">
                  {t('col_serial_num')}:&nbsp;
                </Text>
                <Text size="sm">{data.serial_num}</Text>
              </Stack>
            )}
            {data.model && (
              <Stack gap={1} orientation="horizontal">
                <Text size="sm" color="muted">
                  {t('col_model')}:&nbsp;
                </Text>
                <Text size="sm">{data.model}</Text>
              </Stack>
            )}
            <Stack gap={1} orientation="horizontal">
              <Text size="sm" color="muted">
                {t('col_is_available')}:&nbsp;
              </Text>
              <Text size="sm">{data.is_available ? t('available') : t('unavailable')}</Text>
            </Stack>
            {data.next_pm_at && (
              <Stack gap={1} orientation="horizontal">
                <Text size="sm" color="muted">
                  {t('col_next_pm_at')}:&nbsp;
                </Text>
                <Text size="sm">{formatDate(data.next_pm_at)}</Text>
              </Stack>
            )}
          </>
        }
        footer={
          <Stack gap={2} justifyContent="flex-end" orientation="horizontal">
            <Button
              data-testid={`cancel-delete-instrument-${data.id}`}
              isDisabled={deleteMutation.isPending}
              onPress={() => setIsDeleteOpen(false)}
              visual="unstyled"
            >
              {tGeneral('cancel')}
            </Button>
            <Button
              data-testid={`confirm-delete-instrument-${data.id}`}
              isDisabled={deleteMutation.isPending}
              onPress={handleDeleteConfirm}
              visual="danger"
            >
              {deleteMutation.isPending ? <Loader size={smIconSize} /> : t('delete_yes')}
            </Button>
          </Stack>
        }
        triggerProps={{
          children: <Tooltip content={t('delete_tooltip')} trigger={<Trash2 size={smIconSize} />} />,
          isDisabled,
          size: 'sm',
          visual: 'unstyled',
        }}
      />
    </Stack>
  );
};

export const ActionsCellRenderer = memo(ActionsCellRendererComponent);
